import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const MIN_BARCODE_LENGTH = 4;
const SCANNER_IDLE_MS = 80;
const EXCLUDED_PATHS = new Set([
  "/admin",
  "/admin-login",
  "/cashier",
  "/login",
  "/mobile-scanner",
]);

export function isEditableScannerTarget(target) {
  if (!(target instanceof Element)) return false;
  if (target.isContentEditable || target.contentEditable === "true") return true;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
    ),
  );
}

export function isGlobalScannerRoute(pathname, user) {
  return Boolean(user && !EXCLUDED_PATHS.has(pathname));
}

export function createGlobalBarcodeKeyHandler({
  onBarcode,
  setTimer = window.setTimeout,
  clearTimer = window.clearTimeout,
}) {
  let buffer = "";
  let timer = null;

  const submit = () => {
    const barcode = buffer.trim();
    buffer = "";
    if (timer !== null) clearTimer(timer);
    timer = null;
    if (barcode.length >= MIN_BARCODE_LENGTH) onBarcode(barcode);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (buffer.trim().length >= MIN_BARCODE_LENGTH) event.preventDefault?.();
      submit();
      return;
    }
    if (event.key.length !== 1) return;

    buffer += event.key;
    if (timer !== null) clearTimer(timer);
    timer = setTimer(submit, SCANNER_IDLE_MS);
  };

  return {
    handleKeyDown,
    dispose() {
      if (timer !== null) clearTimer(timer);
      buffer = "";
    },
  };
}

export function getPendingCashierScan(routeState, handledScanId) {
  const barcode = String(routeState?.pendingBarcode || "").trim();
  const scanId = String(routeState?.scanId || "").trim();
  if (!barcode || !scanId || scanId === handledScanId) return null;
  return { barcode, scanId };
}

function readStoredUser() {
  try {
    return JSON.parse(window.localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export default function GlobalBarcodeScanner() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isGlobalScannerRoute(location.pathname, readStoredUser())) {
      return undefined;
    }

    const scanner = createGlobalBarcodeKeyHandler({
      onBarcode: (pendingBarcode) =>
        navigate("/cashier", {
          state: {
            pendingBarcode,
            scanId: `${Date.now()}-${pendingBarcode}`,
          },
        }),
    });
    const handleKeyDown = (event) => {
      if (isEditableScannerTarget(document.activeElement)) return;
      scanner.handleKeyDown(event);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      scanner.dispose();
    };
  }, [location.pathname, navigate]);

  return null;
}
